import { getUserAvatarUrl, getUserLabel } from "../../lib/identity";
import { getColorForEmail, getInitial, uniqueUsers } from "./voter-avatar-utils";

export function AvatarBubble({ user, email, avatar, label, size = 24, colorMap }) {
  const resolvedUser = user || {};
  const resolvedEmail = email || resolvedUser.email || null;
  // Color key: prefer id (a guest has no email), fall back to email. colorMap
  // stays email-keyed for backward compat with buildColorMap(); the id key only
  // routes through getColorForEmail for emailless guests.
  const colorKey = resolvedUser.id || resolvedEmail;
  const resolvedAvatar = avatar || getUserAvatarUrl(resolvedUser);
  // Never fall back to the raw email for the rendered label/tooltip — the email
  // is only a color-hash key (colorKey above), not a display string. A guest's
  // explicit displayName/guestName is fine; otherwise route through the identity
  // helper, which returns the chosen identifier and never an email for a
  // non-email user.
  const resolvedLabel =
    label ||
    getUserLabel({ ...resolvedUser, email: resolvedEmail }) ||
    resolvedUser.guestName ||
    "Guest";
  // Use colorMap if provided (email-keyed), otherwise compute color directly
  // from the color key (id/email).
  const palette =
    (resolvedEmail && colorMap?.[resolvedEmail]) ||
    getColorForEmail(colorKey || resolvedLabel);
  return (
    <div
      className="flex items-center justify-center rounded-full border border-white shadow-sm dark:border-slate-900"
      style={{
        width: size,
        height: size,
        backgroundColor: resolvedAvatar ? "transparent" : palette.bg,
        color: palette.text,
      }}
      title={resolvedLabel}
    >
      {resolvedAvatar ? (
        <img
          src={resolvedAvatar}
          alt={resolvedLabel}
          className="h-full w-full rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="text-[10px] font-semibold">{getInitial(resolvedLabel)}</span>
      )}
    </div>
  );
}

export function AvatarStack({ users, max = 4, size = 20, colorMap, onClick }) {
  const unique = uniqueUsers(users || []);
  const visible = unique.slice(0, max);
  const extra = unique.length - visible.length;
  const isClickable = typeof onClick === "function";
  return (
    <div
      className={`flex items-center -space-x-2${isClickable ? " cursor-pointer rounded-full transition-opacity hover:opacity-80" : ""}`}
      onClick={isClickable ? (e) => { e.stopPropagation(); onClick(unique); } : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onClick(unique); } } : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `View ${unique.length} participant${unique.length !== 1 ? "s" : ""}` : undefined}
    >
      {visible.map((userInfo, index) => (
        <AvatarBubble
          key={userInfo.id || userInfo.email || index}
          user={userInfo}
          size={size}
          colorMap={colorMap}
        />
      ))}
      {extra > 0 && (
        <div
          className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-slate-200 text-[10px] font-semibold text-slate-700 shadow-sm dark:border-slate-900 dark:bg-slate-700 dark:text-slate-200"
          title={`${extra} more`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

export const VOTING_AVATAR_MAX = 10;

export function VotingAvatarStack({ users, max = VOTING_AVATAR_MAX, size = 20, colorMap, onClick }) {
  return <AvatarStack users={users} max={max} size={size} colorMap={colorMap} onClick={onClick} />;
}
